import { commercialBlogPosts } from '@/lib/blogDataCommercial';

export type BlogPostId =
  | 'blog1'
  | 'blog2'
  | 'blog3'
  | 'blog4'
  | 'blog5'
  | 'blog6'
  | 'blog7'
  | 'blog8'
  | 'blog9'
  | 'blog10'
  | 'blog11'
  | 'blog12';

export interface BlogPostSection {
  heading: {
    tr: string;
    en: string;
  };
  paragraphs: {
    tr: string[];
    en: string[];
  };
}

export interface BlogPost {
  id: BlogPostId;
  slug: string;
  category: {
    tr: string;
    en: string;
  };
  title: {
    tr: string;
    en: string;
  };
  excerpt: {
    tr: string;
    en: string;
  };
  date: {
    tr: string;
    en: string;
  };
  /** ISO 8601 — BlogPosting JSON-LD `datePublished` */
  publishedIso: string;
  seoDescription: {
    tr: string;
    en: string;
  };
  intro: {
    tr: string;
    en: string;
  };
  /** SEO / AEO keyword tags */
  tags?: string[];
  sections: BlogPostSection[];
}

const coreBlogPosts: BlogPost[] = [
  {
    id: 'blog1',
    slug: 'ux-design-in-ai-products',
    category: {
      tr: 'Tasarım',
      en: 'Design',
    },
    title: {
      tr: 'AI Ürünlerinde UX Tasarımının Önemi',
      en: 'The Importance of UX Design in AI Products',
    },
    excerpt: {
      tr: 'Yapay zeka ürünlerinde kullanıcı deneyimini nasıl mükemmelleştiriyoruz? Pratik yaklaşımlar ve örnekler.',
      en: 'How we perfect user experience in AI products. Practical approaches and examples.',
    },
    date: {
      tr: '28 Mar 2026',
      en: 'Mar 28, 2026',
    },
    publishedIso: '2026-03-28',
    seoDescription: {
      tr: 'AI ürünlerinde güvenilir, anlaşılır ve ölçülebilir kullanıcı deneyimi tasarlamak için temel UX prensipleri.',
      en: 'Core UX principles to design trustworthy, understandable, and measurable AI product experiences.',
    },
    tags: ['AI', 'AI product development', 'UX design'],
    intro: {
      tr: 'AI destekli ürünlerde model performansı tek başına yeterli değildir. Kullanıcılar, sistemin neden bu sonucu ürettiğini anlamadığında güven hızla düşer.',
      en: 'Model performance alone is not enough in AI-powered products. Trust drops quickly when users do not understand why the system generated a result.',
    },
    sections: [
      {
        heading: {
          tr: 'Açıklanabilirlik olmadan güven oluşmaz',
          en: 'No trust without explainability',
        },
        paragraphs: {
          tr: [
            'Tahmin üreten bir arayüzde sadece skor göstermek yerine, kararı etkileyen ana sinyalleri görselleştirmek gerekir.',
            'Kullanıcıya "bu öneri neden geldi" sorusunun net cevabını vermek, ürünün benimsenmesini ciddi biçimde artırır.',
          ],
          en: [
            'In prediction interfaces, showing only a score is insufficient; you should visualize the core signals that influenced the decision.',
            'Clearly answering "why this recommendation" significantly increases product adoption.',
          ],
        },
      },
      {
        heading: {
          tr: 'Hata durumlarını tasarlayın',
          en: 'Design for failure states',
        },
        paragraphs: {
          tr: [
            'AI çıktıları her zaman doğru olmayabilir. Bu yüzden itiraz, düzeltme ve geri bildirim akışları ürünün ayrılmaz parçası olmalıdır.',
            'Başarısız durumları görünmez kılmak yerine iyi tasarlanmış fallback deneyimleri sunmak kullanıcı memnuniyetini korur.',
          ],
          en: [
            'AI outputs are not always correct. Objection, correction, and feedback flows must be first-class parts of the product.',
            'Instead of hiding failures, well-designed fallback experiences preserve user satisfaction.',
          ],
        },
      },
      {
        heading: {
          tr: 'Ölçüm: doğruluk kadar kullanım da önemli',
          en: 'Measurement: usage matters as much as accuracy',
        },
        paragraphs: {
          tr: [
            'Sadece model doğruluğunu değil, öneri kabul oranı, görev tamamlama süresi ve kullanıcı güven skoru gibi UX metriklerini de izleyin.',
            'Başarılı AI ürünleri, teknik kaliteyi davranışsal metriklerle birlikte optimize eden ürünlerdir.',
          ],
          en: [
            'Track not just model accuracy but also recommendation acceptance rate, task completion time, and user trust scores.',
            'Great AI products optimize technical quality together with behavioral metrics.',
          ],
        },
      },
    ],
  },
  {
    id: 'blog2',
    slug: 'saas-scaling-strategies',
    category: {
      tr: 'Mühendislik',
      en: 'Engineering',
    },
    title: {
      tr: 'SaaS Ölçeklendirme Stratejileri',
      en: 'SaaS Scaling Strategies',
    },
    excerpt: {
      tr: 'Ürününüz büyürken teknik altyapınızı nasıl ölçeklendirmelisiniz? Deneyimlerimizden dersler.',
      en: 'How should you scale your technical infrastructure as your product grows? Lessons from our experience.',
    },
    date: {
      tr: '22 Mar 2026',
      en: 'Mar 22, 2026',
    },
    publishedIso: '2026-03-22',
    seoDescription: {
      tr: 'SaaS ürünlerinde erken ölçekleme hatalarından kaçınmak için mimari, veri ve operasyon stratejileri.',
      en: 'Architecture, data, and operations strategies to avoid early scaling mistakes in SaaS products.',
    },
    tags: ['SaaS', 'SaaS geliştirme'],
    intro: {
      tr: 'SaaS ölçeklendirme, sadece daha fazla sunucu eklemek değildir. Kritik olan, darboğazı doğru noktada tespit etmek ve adım adım çözmektir.',
      en: 'SaaS scaling is not just adding more servers. The real challenge is locating the right bottleneck and solving it in controlled steps.',
    },
    sections: [
      {
        heading: {
          tr: 'Önce ölç, sonra böl',
          en: 'Measure first, split second',
        },
        paragraphs: {
          tr: [
            'Mikroservise erken geçiş çoğu ekipte operasyon yükünü artırır. Önce ölçüm altyapısını tamamlayıp monoliti optimize etmek daha sağlıklıdır.',
            'API latency, DB sorgu süreleri ve kuyruk gecikmeleri görünür hale gelmeden mimari karar almak risktir.',
          ],
          en: [
            'Moving to microservices too early often increases operational burden. Complete observability and optimize the monolith first.',
            'Architectural decisions are risky before API latency, DB query times, and queue delays are visible.',
          ],
        },
      },
      {
        heading: {
          tr: 'Veri katmanını erken güçlendirin',
          en: 'Strengthen the data layer early',
        },
        paragraphs: {
          tr: [
            'Yavaşlayan ürünlerin büyük kısmında sorun uygulama değil veri modelidir. Doğru indeksleme, sorgu planı analizi ve cache stratejisi kritik fark yaratır.',
            'Tenant bazlı izolasyon, büyüme aşamasında güvenlik ve performans dengesini korumada önemlidir.',
          ],
          en: [
            'In many slowing products, the core issue is the data model, not the app layer. Indexing, query plan analysis, and caching are decisive.',
            'Tenant-level isolation is key to preserving security and performance balance during growth.',
          ],
        },
      },
      {
        heading: {
          tr: 'Operasyonel otomasyon şart',
          en: 'Operational automation is mandatory',
        },
        paragraphs: {
          tr: [
            'CI/CD, altyapı kodu ve otomatik rollback mekanizmaları olmadan ölçek büyüdükçe hata maliyeti katlanır.',
            'SaaS ekipleri için hızın sürdürülebilir olması, release sürecinin öngörülebilir olmasına bağlıdır.',
          ],
          en: [
            'Without CI/CD, infrastructure as code, and automatic rollback mechanisms, failure cost grows exponentially with scale.',
            'Sustainable velocity in SaaS teams depends on predictable release pipelines.',
          ],
        },
      },
    ],
  },
  {
    id: 'blog3',
    slug: 'modern-fullstack-tooling',
    category: {
      tr: 'Teknoloji',
      en: 'Technology',
    },
    title: {
      tr: 'Full-Stack Geliştirmede Modern Araçlar',
      en: 'Modern Tools in Full-Stack Development',
    },
    excerpt: {
      tr: '2026\'da kullandığımız araçlar ve neden bu teknoloji yığınını tercih ediyoruz.',
      en: 'The tools we use in 2026 and why we chose this tech stack.',
    },
    date: {
      tr: '15 Mar 2026',
      en: 'Mar 15, 2026',
    },
    publishedIso: '2026-03-15',
    seoDescription: {
      tr: 'Modern full-stack ekiplerinde hız, bakım kolaylığı ve kalite için araç seçimi yaklaşımı.',
      en: 'Tooling approach for speed, maintainability, and quality in modern full-stack teams.',
    },
    intro: {
      tr: 'Araç seçimi bir moda konusu değil, maliyet ve hız dengesidir. Yanlış seçim teknik borcu erken büyütür.',
      en: 'Tooling is not about trends, it is about balancing cost and speed. Poor choices create technical debt early.',
    },
    sections: [
      {
        heading: {
          tr: 'Frontend: hız ve güvenli tip sistemi',
          en: 'Frontend: speed with type safety',
        },
        paragraphs: {
          tr: [
            'Vite + TypeScript kombinasyonu geliştirme döngüsünü ciddi hızlandırır. Tip güvenliği, refactor sırasında hatayı erkenden yakalar.',
            'UI tarafında bileşen kütüphanesi ve tasarım tokenları birlikte yönetilmelidir.',
          ],
          en: [
            'Vite + TypeScript significantly accelerates the dev loop. Type safety catches issues early during refactors.',
            'On the UI side, component libraries and design tokens should be managed together.',
          ],
        },
      },
      {
        heading: {
          tr: 'Backend: gözlemlenebilirlik varsayılan olmalı',
          en: 'Backend: observability by default',
        },
        paragraphs: {
          tr: [
            'Servisler yalnızca çalışmamalı, anlaşılabilir olmalıdır. Log, metric ve trace birlikte ele alındığında hata çözümü hızlanır.',
            'API sözleşmelerini şemalarla yönetmek ekipler arası entegrasyon maliyetini düşürür.',
          ],
          en: [
            'Services should not only run but also be understandable. Logs, metrics, and traces together speed up incident resolution.',
            'Managing API contracts with schemas reduces cross-team integration cost.',
          ],
        },
      },
      {
        heading: {
          tr: 'Kalite: test katmanlarını net ayırın',
          en: 'Quality: separate test layers clearly',
        },
        paragraphs: {
          tr: [
            'Unit, integration ve e2e testlerin amaçları farklıdır. Hepsini aynı iş için kullanmak zaman ve bakım kaybı yaratır.',
            'Doğru test piramidi ile release güvenini artırırken geliştirme hızını da koruyabilirsiniz.',
          ],
          en: [
            'Unit, integration, and e2e tests serve different goals. Using all for the same concern wastes time and maintenance effort.',
            'A proper test pyramid increases release confidence while preserving development speed.',
          ],
        },
      },
    ],
  },
  {
    id: 'blog4',
    slug: 'mvp-development-guide',
    category: {
      tr: 'Strateji',
      en: 'Strategy',
    },
    title: {
      tr: 'Startup\'lar İçin MVP Geliştirme Rehberi',
      en: 'MVP Development Guide for Startups',
    },
    excerpt: {
      tr: 'Minimum uygulanabilir ürün geliştirirken dikkat edilmesi gereken kritik noktalar.',
      en: 'Critical points to consider when developing a minimum viable product.',
    },
    date: {
      tr: '8 Mar 2026',
      en: 'Mar 8, 2026',
    },
    publishedIso: '2026-03-08',
    seoDescription: {
      tr: 'MVP sürecinde kapsamı doğru çizmek, hızlı öğrenmek ve erken validasyon almak için pratik yaklaşım.',
      en: 'Practical approach to scope MVP correctly, learn fast, and capture early validation.',
    },
    tags: ['MVP', 'MVP geliştirme'],
    intro: {
      tr: 'MVP, küçük ürün değil; doğru riski test eden ürün demektir. Amaç hızlıca öğrenmek ve bir sonraki adımı netleştirmektir.',
      en: 'An MVP is not just a smaller product; it is a product that tests the right risk. The goal is to learn fast and clarify the next move.',
    },
    sections: [
      {
        heading: {
          tr: 'Önce risk haritası çıkarın',
          en: 'Map risks before features',
        },
        paragraphs: {
          tr: [
            'Pazar riski, kullanım riski ve teknik risk aynı anda çözülmez. MVP kapsamında en kritik varsayımı hedefleyin.',
            'Her özelliğin doğruladığı hipotezi yazmadan backlog oluşturmak odak kaybına neden olur.',
          ],
          en: [
            'Market, usage, and technical risks are not solved simultaneously. Target the most critical assumption in the MVP scope.',
            'Creating backlog items without explicit hypotheses causes focus loss.',
          ],
        },
      },
      {
        heading: {
          tr: 'Ölçülebilir başarı tanımı koyun',
          en: 'Define measurable success',
        },
        paragraphs: {
          tr: [
            'Aktivasyon oranı, 7 günlük geri dönüş ve ücretli deneme dönüşümü gibi metrikler MVP başarısını objektif değerlendirir.',
            'Net eşikler olmadan yapılan yorumlar, yanlış pozitif başarı algısı yaratır.',
          ],
          en: [
            'Metrics like activation rate, 7-day retention, and trial-to-paid conversion objectively evaluate MVP success.',
            'Without clear thresholds, interpretation often creates false-positive success signals.',
          ],
        },
      },
      {
        heading: {
          tr: 'Teknik borcu kontrollü alın',
          en: 'Take technical debt intentionally',
        },
        paragraphs: {
          tr: [
            'MVP aşamasında bazı kısa yollar doğaldır. Kritik olan, hangi borcun ne zaman kapatılacağını baştan planlamaktır.',
            'Bilinçli teknik borç, hız kazandırır; görünmez teknik borç ise büyümeyi bloklar.',
          ],
          en: [
            'Some shortcuts are expected at MVP stage. What matters is planning when each debt item will be retired.',
            'Intentional debt can buy speed; invisible debt eventually blocks growth.',
          ],
        },
      },
    ],
  },
  {
    id: 'blog5',
    slug: 'mobile-app-performance-optimization',
    category: {
      tr: 'Mobil',
      en: 'Mobile',
    },
    title: {
      tr: 'Mobil Uygulama Performans Optimizasyonu',
      en: 'Mobile App Performance Optimization',
    },
    excerpt: {
      tr: 'React Native ve Flutter ile yüksek performanslı mobil deneyimler oluşturma teknikleri.',
      en: 'Techniques for creating high-performance mobile experiences with React Native and Flutter.',
    },
    date: {
      tr: '1 Mar 2026',
      en: 'Mar 1, 2026',
    },
    publishedIso: '2026-03-01',
    seoDescription: {
      tr: 'Mobil uygulamalarda açılış süresi, akıcılık ve ağ yönetimi için performans odaklı pratikler.',
      en: 'Performance-first practices for startup time, smoothness, and network handling in mobile apps.',
    },
    intro: {
      tr: 'Mobil performans kullanıcı sadakatini doğrudan etkiler. 1-2 saniyelik gecikme bile dönüşümde ciddi kayıplara yol açabilir.',
      en: 'Mobile performance directly impacts user retention. Even 1-2 seconds of delay can significantly hurt conversion.',
    },
    sections: [
      {
        heading: {
          tr: 'Açılış performansı',
          en: 'Startup performance',
        },
        paragraphs: {
          tr: [
            'Uygulama ilk açılışında kritik olmayan modülleri gecikmeli yüklemek algılanan hızı artırır.',
            'Splash sonrası boş ekran süresi minimumda tutulmalı, kullanıcıya her adımda görsel geri bildirim verilmelidir.',
          ],
          en: [
            'Deferring non-critical modules on initial load improves perceived performance.',
            'Post-splash blank time should be minimized and users should get visual feedback at each step.',
          ],
        },
      },
      {
        heading: {
          tr: 'Liste ve görsel optimizasyonu',
          en: 'List and image optimization',
        },
        paragraphs: {
          tr: [
            'Uzun listelerde sanallaştırma, gereksiz yeniden render maliyetini düşürür.',
            'Görsellerde doğru boyutlandırma ve lazy loading, hem bellek hem ağ kullanımını iyileştirir.',
          ],
          en: [
            'In long lists, virtualization reduces unnecessary re-render costs.',
            'Proper image sizing and lazy loading improve both memory and network usage.',
          ],
        },
      },
      {
        heading: {
          tr: 'Ağ ve offline yaklaşımı',
          en: 'Network and offline strategy',
        },
        paragraphs: {
          tr: [
            'İstek birleştirme, cache politikaları ve retry stratejileri ağ dalgalanmalarında deneyimi stabil tutar.',
            'Offline-first yaklaşımı, özellikle saha ekipleri için kritik iş akışlarını kesintisiz hale getirir.',
          ],
          en: [
            'Request batching, cache policies, and retry strategies keep UX stable under network fluctuations.',
            'Offline-first design keeps critical workflows uninterrupted, especially for field teams.',
          ],
        },
      },
    ],
  },
  {
    id: 'blog6',
    slug: 'data-security-and-compliance',
    category: {
      tr: 'Güvenlik',
      en: 'Security',
    },
    title: {
      tr: 'Veri Güvenliği ve Compliance',
      en: 'Data Security and Compliance',
    },
    excerpt: {
      tr: 'Modern uygulamalarda veri güvenliği best practice\'leri ve KVKK uyumu.',
      en: 'Data security best practices and compliance in modern applications.',
    },
    date: {
      tr: '22 Şub 2026',
      en: 'Feb 22, 2026',
    },
    publishedIso: '2026-02-22',
    seoDescription: {
      tr: 'SaaS ve AI ürünlerinde veri güvenliği, erişim kontrolü ve uyumluluk için uygulanabilir çerçeve.',
      en: 'An actionable framework for data security, access control, and compliance in SaaS and AI products.',
    },
    intro: {
      tr: 'Güvenlik, ürün tamamlandıktan sonra eklenen bir katman değildir. Mimari kararların içine baştan yerleşmelidir.',
      en: 'Security is not a layer added after the product is finished. It must be built into architectural decisions from day one.',
    },
    sections: [
      {
        heading: {
          tr: 'Yetki modeli ve erişim kontrolü',
          en: 'Authorization and access control',
        },
        paragraphs: {
          tr: [
            'Rol bazlı erişim tek başına yeterli olmayabilir. Hassas veriler için bağlam bazlı kontroller eklemek gerekir.',
            'Audit log altyapısı, hem güvenlik olaylarını hem de uyumluluk süreçlerini hızlandırır.',
          ],
          en: [
            'Role-based access may be insufficient alone. Sensitive data often needs context-based controls as well.',
            'A strong audit log system accelerates both incident response and compliance workflows.',
          ],
        },
      },
      {
        heading: {
          tr: 'Veri yaşam döngüsü yönetimi',
          en: 'Data lifecycle management',
        },
        paragraphs: {
          tr: [
            'Hangi verinin ne kadar süre tutulacağı net tanımlanmalıdır. Gereksiz veri, hem maliyet hem risk üretir.',
            'Silme, anonimleştirme ve yedekleme politikaları regülasyonlarla uyumlu olacak şekilde otomasyona bağlanmalıdır.',
          ],
          en: [
            'Retention windows must be explicit. Unnecessary data creates both cost and risk.',
            'Deletion, anonymization, and backup policies should be automated in line with regulations.',
          ],
        },
      },
      {
        heading: {
          tr: 'Güvenlik testlerini sürekli hale getirin',
          en: 'Make security testing continuous',
        },
        paragraphs: {
          tr: [
            'SAST, dependency scanning ve temel pentest kontrolleri release pipeline\'ının parçası olmalıdır.',
            'Sürekli güvenlik yaklaşımı, tek seferlik denetimlere göre daha düşük toplam risk sağlar.',
          ],
          en: [
            'SAST, dependency scanning, and baseline pentest checks should be part of the release pipeline.',
            'A continuous security approach yields lower overall risk than one-off audits.',
          ],
        },
      },
    ],
  },
];

export const blogPosts: BlogPost[] = [...coreBlogPosts, ...commercialBlogPosts];

export const blogPostsPreview = blogPosts.slice(0, 3);

export const getBlogPostBySlug = (slug: string) => {
  return blogPosts.find((post) => post.slug === slug);
};
